import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Clock } from 'lucide-react';
import { useRows } from '@/hooks/useResource';
import { callRpc } from '@/api/resources';
import { CONSENT_TYPES } from '@/lib/consentTypes';
import { toast } from '@/lib/toast';

/**
 * How long each consent type stays valid by default (owner, 2026-09-21: "consent form and all
 * other digital records shall need to have validity"; migration 0075). A consent issued without
 * its own end date gets this many months from its start.
 */
export function ConsentValidityRules({ mayEdit }: { mayEdit: boolean }) {
  const queryClient = useQueryClient();
  const rules = useRows<{ consent_type: string; validity_months: number }>('consent_validity_rules', {});
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [open, setOpen] = useState(false);

  async function save(type: string) {
    const months = Number(draft[type]);
    if (!Number.isInteger(months) || months < 1 || months > 600) { toast.error('Give a whole number of months between 1 and 600.'); return; }
    try {
      await callRpc('fn_set_consent_validity', { p_consent_type: type, p_validity_months: months });
      await queryClient.invalidateQueries({ queryKey: ['consent_validity_rules'] });
      setDraft((now) => { const next = { ...now }; delete next[type]; return next; });
      toast.success('Saved. New consents of this type use it; existing ones keep their dates.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'That could not be saved.');
    }
  }

  return (
    <details className="card validity-rules" onToggle={(event) => setOpen((event.target as HTMLDetailsElement).open)} open={open}>
      <summary><Clock size={15} /> How long each consent is valid</summary>
      <table className="simple-table">
        <thead><tr><th>Consent</th><th>Valid for (months)</th><th /></tr></thead>
        <tbody>
          {CONSENT_TYPES.map((type) => {
            const current = rules.data?.find((rule) => rule.consent_type === type.id)?.validity_months ?? 12;
            const value = draft[type.id] ?? String(current);
            return (
              <tr key={type.id}>
                <td>{type.label}</td>
                <td style={{ width: 170 }}>
                  <input disabled={!mayEdit} inputMode="numeric" onChange={(event) => setDraft({ ...draft, [type.id]: event.target.value.replace(/\D/g, '') })}
                         value={value} />
                </td>
                <td style={{ width: 90 }}>
                  {mayEdit && draft[type.id] !== undefined && draft[type.id] !== String(current) && (
                    <button className="primary" onClick={() => void save(type.id)} type="button">Save</button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </details>
  );
}
